/**
 * Connection Retry Utility
 * Implements retry logic for connection status checks with exponential backoff
 */

export interface RetryOptions {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  retryCondition?: (error: any) => boolean;
}

export interface RetryResult<T> {
  success: boolean;
  data?: T;
  error?: any;
  attempts: number;
  totalTime: number;
}

export class ConnectionRetryManager {
  private static defaultOptions: RetryOptions = {
    maxRetries: 3,
    initialDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
    backoffMultiplier: 2,
    retryCondition: (error: any) => {
      // Retry on network errors, timeouts, and server errors
      return (
        error?.code === 'NETWORK_ERROR' ||
        error?.code === 'TIMEOUT' ||
        error?.status >= 500 ||
        error?.message?.includes('timeout') ||
        error?.message?.includes('network')
      );
    }
  };

  /**
   * Execute a function with retry logic
   */
  static async withRetry<T>(
    operation: () => Promise<T>,
    options: Partial<RetryOptions> = {}
  ): Promise<RetryResult<T>> {
    const config = { ...this.defaultOptions, ...options };
    const startTime = Date.now();
    let lastError: any;
    
    for (let attempt = 1; attempt <= config.maxRetries + 1; attempt++) {
      try {
        const result = await operation();
        
        return {
          success: true,
          data: result,
          attempts: attempt,
          totalTime: Date.now() - startTime
        };
      } catch (error) {
        lastError = error;
        
        // Don't retry on the last attempt or if retry condition fails
        if (attempt === config.maxRetries + 1 || !config.retryCondition!(error)) {
          break;
        }
        
        // Calculate delay with exponential backoff
        const delay = Math.min(
          config.initialDelay * Math.pow(config.backoffMultiplier, attempt - 1),
          config.maxDelay
        );
        
        console.warn(`Connection attempt ${attempt} failed, retrying in ${delay}ms:`, error?.message);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    return {
      success: false,
      error: lastError,
      attempts: config.maxRetries + 1,
      totalTime: Date.now() - startTime
    };
  }

  /**
   * Check connection status with retry logic
   */
  static async checkConnectionWithRetry(
    checkFunction: () => Promise<any>,
    customOptions?: Partial<RetryOptions>
  ): Promise<RetryResult<any>> {
    const options = {
      ...this.defaultOptions,
      ...customOptions,
      retryCondition: (error: any) => {
        // More specific retry conditions for connection checks
        const shouldRetry = 
          error?.code === 'TC_TIMEOUT' ||
          error?.code === 'CONNECTION_ERROR' ||
          error?.status === 502 ||
          error?.status === 503 ||
          error?.status === 504 ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('refused');
        
        // Don't retry if TC is definitely uninstalled
        const isUninstalled = 
          error?.code === 'TC_UNINSTALLED' ||
          error?.code === 'TC_NOT_FOUND' ||
          error?.status === 404;
        
        return shouldRetry && !isUninstalled;
      }
    };

    return this.withRetry(checkFunction, options);
  }

  /**
   * Health check with progressive timeout
   */
  static async healthCheckWithTimeout(
    healthCheckFunction: () => Promise<any>,
    timeouts: number[] = [5000, 10000, 15000] // Progressive timeouts in ms
  ): Promise<RetryResult<any>> {
    const startTime = Date.now();
    let lastError: any;

    for (let i = 0; i < timeouts.length; i++) {
      try {
        // Create a timeout promise
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error(`Health check timeout after ${timeouts[i]}ms`));
          }, timeouts[i]);
        });

        // Race between health check and timeout
        const result = await Promise.race([
          healthCheckFunction(),
          timeoutPromise
        ]);

        return {
          success: true,
          data: result,
          attempts: i + 1,
          totalTime: Date.now() - startTime
        };
      } catch (error) {
        lastError = error;
        console.warn(`Health check attempt ${i + 1} failed (timeout: ${timeouts[i]}ms):`, error?.message);

        // If this is a definitive "not found" error, don't continue
        if (error?.status === 404 || error?.message?.includes('not found')) {
          break;
        }
      }
    }

    return {
      success: false,
      error: lastError,
      attempts: timeouts.length,
      totalTime: Date.now() - startTime
    };
  }

  /**
   * Exponential backoff for status monitoring
   */
  static createBackoffScheduler(
    checkFunction: () => Promise<void>,
    onSuccess?: () => void,
    onFailure?: (error: any) => void
  ) {
    let attempt = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isRunning = false;

    const schedule = async () => {
      if (!isRunning) return;

      try {
        await checkFunction();
        attempt = 0; // Reset on success
        onSuccess?.();
        
        // Schedule next check with base interval (30 seconds)
        if (isRunning) {
          timeoutId = setTimeout(schedule, 30000);
        }
      } catch (error) {
        onFailure?.(error);
        attempt++;
        
        // Calculate backoff delay (max 5 minutes)
        const delay = Math.min(1000 * Math.pow(2, attempt), 300000);
        
        console.warn(`Status check failed, retrying in ${delay / 1000} seconds:`, error?.message);
        
        if (isRunning) {
          timeoutId = setTimeout(schedule, delay);
        }
      }
    };

    return {
      start: () => {
        if (!isRunning) {
          isRunning = true;
          schedule();
        }
      },
      stop: () => {
        isRunning = false;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      },
      isRunning: () => isRunning
    };
  }
}

export default ConnectionRetryManager;